import { useState, useMemo } from 'react';
import { assertNever } from '@signalvote/shared';
import type { VendorSummaryItem } from '@signalvote/shared';
import { TrendIcon, trendSortRank } from './TrendIcon';
import { SortIcon } from './SortIcon';
import type { SortDirection } from './SortIcon';
import { getReliabilityColor, getSurveyColor } from './colorUtils';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type SortField = 'vendor' | 'canvassers' | 'hours' | 'doors' | 'contacts' | 'doorsPerHour' | 'reachRate' | 'reliability' | 'survey' | 'trend';

function getDoorsPerHour(v: VendorSummaryItem): number | null {
  return v.totalHours > 0 ? Math.round((v.totalDoorsKnocked / v.totalHours) * 10) / 10 : null;
}

function getReachRate(v: VendorSummaryItem): number | null {
  return v.totalDoorsKnocked > 0
    ? Math.round((v.totalContactsMade / v.totalDoorsKnocked) * 1000) / 10
    : null;
}

function getHighlightClass(value: number | null, allValues: (number | null)[]): string {
  if (value === null) return '';
  const nonNull = allValues.filter((v): v is number => v !== null);
  if (nonNull.length < 2) return '';
  const max = Math.max(...nonNull);
  const min = Math.min(...nonNull);
  if (max === min) return '';
  if (value === max) return 'text-green-600';
  if (value === min) return 'text-red-600';
  return '';
}

interface VendorComparisonTableProps {
  vendors: VendorSummaryItem[];
  onVendorClick: (vendorId: string) => void;
  isLoading?: boolean;
}

export function VendorComparisonTable({ vendors, onVendorClick, isLoading }: VendorComparisonTableProps) {
  const [sortField, setSortField] = useState<SortField>('vendor');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedVendors = useMemo(() => {
    return [...vendors].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'vendor':
          cmp = a.vendorName.localeCompare(b.vendorName);
          break;
        case 'canvassers':
          cmp = a.canvasserCount - b.canvasserCount;
          break;
        case 'hours':
          cmp = a.totalHours - b.totalHours;
          break;
        case 'doors':
          cmp = a.totalDoorsKnocked - b.totalDoorsKnocked;
          break;
        case 'contacts':
          cmp = a.totalContactsMade - b.totalContactsMade;
          break;
        case 'doorsPerHour':
          cmp = (getDoorsPerHour(a) ?? -1) - (getDoorsPerHour(b) ?? -1);
          break;
        case 'reachRate':
          cmp = (getReachRate(a) ?? -1) - (getReachRate(b) ?? -1);
          break;
        case 'reliability':
          cmp = (a.reliabilityRate ?? -1) - (b.reliabilityRate ?? -1);
          break;
        case 'survey':
          cmp = (a.surveyCompletionRate ?? -1) - (b.surveyCompletionRate ?? -1);
          break;
        case 'trend':
          cmp = trendSortRank(a.trendDoorsPerHour) - trendSortRank(b.trendDoorsPerHour);
          break;
        default:
          assertNever(sortField);
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [vendors, sortField, sortDirection]);

  if (!isLoading && vendors.length <= 1) return null;

  // Precompute highlight values
  const allDoors = vendors.map((v) => v.totalDoorsKnocked);
  const allContacts = vendors.map((v) => v.totalContactsMade);
  const allHours = vendors.map((v) => v.totalHours);
  const allDoorsPerHour = vendors.map(getDoorsPerHour);
  const allReachRates = vendors.map(getReachRate);
  const allReliability = vendors.map((v) => v.reliabilityRate);
  const allSurvey = vendors.map((v) => v.surveyCompletionRate);
  const allCanvassers = vendors.map((v) => v.canvasserCount);

  return (
    <Card>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">
                    <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('vendor')}>
                      Vendor <SortIcon field="vendor" activeField={sortField} direction={sortDirection} />
                    </button>
                  </TableHead>
                  <TableHead className="w-[90px]">
                    <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('canvassers')}>
                      Canvassers <SortIcon field="canvassers" activeField={sortField} direction={sortDirection} />
                    </button>
                  </TableHead>
                  <TableHead className="w-[70px]">
                    <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('hours')}>
                      Hours <SortIcon field="hours" activeField={sortField} direction={sortDirection} />
                    </button>
                  </TableHead>
                  <TableHead className="w-[70px]">
                    <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('doors')}>
                      Doors <SortIcon field="doors" activeField={sortField} direction={sortDirection} />
                    </button>
                  </TableHead>
                  <TableHead className="w-[80px]">
                    <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('contacts')}>
                      Contacts <SortIcon field="contacts" activeField={sortField} direction={sortDirection} />
                    </button>
                  </TableHead>
                  <TableHead className="w-[80px]">
                    <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('doorsPerHour')}>
                      Doors/Hr <SortIcon field="doorsPerHour" activeField={sortField} direction={sortDirection} />
                    </button>
                  </TableHead>
                  <TableHead className="w-[80px]">
                    <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('reachRate')}>
                      Reach % <SortIcon field="reachRate" activeField={sortField} direction={sortDirection} />
                    </button>
                  </TableHead>
                  <TableHead className="w-[90px]">
                    <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('reliability')}>
                      Reliability <SortIcon field="reliability" activeField={sortField} direction={sortDirection} />
                    </button>
                  </TableHead>
                  <TableHead className="w-[80px]">
                    <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('survey')}>
                      Survey % <SortIcon field="survey" activeField={sortField} direction={sortDirection} />
                    </button>
                  </TableHead>
                  <TableHead className="w-[70px]">
                    <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('trend')}>
                      Trend <SortIcon field="trend" activeField={sortField} direction={sortDirection} />
                    </button>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedVendors.map((v) => {
                  const doorsPerHour = getDoorsPerHour(v);
                  const reachRate = getReachRate(v);

                  return (
                    <TableRow
                      key={v.vendorId}
                      className="cursor-pointer even:bg-muted/30 hover:bg-accent/50 transition-colors duration-150"
                      onClick={() => onVendorClick(v.vendorId)}
                    >
                      <TableCell className="font-medium">{v.vendorName}</TableCell>
                      <TableCell className={`tabular-nums ${getHighlightClass(v.canvasserCount, allCanvassers)}`} data-testid={`canvassers-${v.vendorId}`}>
                        {v.canvasserCount}
                      </TableCell>
                      <TableCell className={`tabular-nums ${getHighlightClass(v.totalHours, allHours)}`} data-testid={`hours-${v.vendorId}`}>
                        {v.totalHours.toFixed(1)}
                      </TableCell>
                      <TableCell className={`tabular-nums ${getHighlightClass(v.totalDoorsKnocked, allDoors)}`} data-testid={`doors-${v.vendorId}`}>
                        {v.totalDoorsKnocked}
                      </TableCell>
                      <TableCell className={`tabular-nums ${getHighlightClass(v.totalContactsMade, allContacts)}`} data-testid={`contacts-${v.vendorId}`}>
                        {v.totalContactsMade}
                      </TableCell>
                      <TableCell className={`tabular-nums ${getHighlightClass(doorsPerHour, allDoorsPerHour)}`} data-testid={`doorsPerHour-${v.vendorId}`}>
                        {doorsPerHour !== null ? doorsPerHour.toFixed(1) : 'N/A'}
                      </TableCell>
                      <TableCell className={`tabular-nums ${getHighlightClass(reachRate, allReachRates)}`} data-testid={`reachRate-${v.vendorId}`}>
                        {reachRate !== null ? `${reachRate.toFixed(1)}%` : 'N/A'}
                      </TableCell>
                      <TableCell className="tabular-nums" data-testid={`reliability-${v.vendorId}`}>
                        {v.reliabilityRate !== null ? (
                          <span className={getReliabilityColor(v.reliabilityRate)}>
                            {v.reliabilityRate.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums" data-testid={`survey-${v.vendorId}`}>
                        {v.surveyCompletionRate !== null ? (
                          <span className={getSurveyColor(v.surveyCompletionRate)}>
                            {v.surveyCompletionRate.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </TableCell>
                      <TableCell data-testid={`trend-${v.vendorId}`}>
                        <TrendIcon direction={v.trendDoorsPerHour} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
